import { Navigate, useLocation } from 'react-router-dom';

/**
 * Decodes a JWT's payload segment and checks whether the token has expired.
 * Does NOT verify the signature — this is a client-side UX guard only.
 *
 * @param {string} token - A JWT string (header.payload.signature)
 * @returns {boolean} true if the token is expired or malformed, false if still valid
 */
export function isTokenExpired(token) {
  try {
    // A valid JWT has exactly 3 dot-separated segments
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    // The payload is Base64URL-encoded — replace URL-safe chars before decoding
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    );

    // If there's no `exp` field, treat the token as never-expiring
    if (typeof payload.exp !== 'number') return false;

    // `exp` is in seconds; Date.now() is in milliseconds
    return payload.exp * 1000 < Date.now();
  } catch {
    // If anything goes wrong (malformed token, bad base64, etc.) treat as expired
    return true;
  }
}

/**
 * Guard for routes that require authentication.
 * Checks both token existence AND expiry. If either condition fails,
 * clears stale session data and redirects to the login page.
 */
export const RequireAuth = ({ children }) => {
  const token = localStorage.getItem('staff_token');
  const location = useLocation();

  if (!token || isTokenExpired(token)) {
    // Proactively clear stale auth data so the user gets a clean login screen
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_refresh_token');
    localStorage.removeItem('staff_profile');

    return <Navigate to="/staff" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * Guard for routes that should ONLY be accessible when NOT logged in (e.g. Login page).
 * If user is already logged in (with a non-expired token), redirect to dashboard.
 */
export const PublicOnly = ({ children }) => {
  const token = localStorage.getItem('staff_token');

  if (token && !isTokenExpired(token)) {
    return <Navigate to="/staff/dashboard" replace />;
  }

  return children;
};
